/**
 * Every act against the selected node, in the composer, as four modes — Ask,
 * Reason, Investigate, Record — so the node card can be the reading (D347).
 *
 * THE CARD READS, THE COMPOSER ACTS. The card carried the claim, where it
 * stands, and then a wall of presses — the source check, six verbs, three wide
 * passes, a second opinion, the free pair — before the model's own account,
 * folded at the foot. A reader asked to see the ratings and the account and
 * found the presses first. So the presses moved to the composer, which is pinned
 * at the column's foot whatever the card's scroll: every act is one row from the
 * input, on every node, and the card leads with what the record says.
 *
 * Every press here is the one the card made, unchanged — the same component
 * where one existed (`VerificationPanel`'s press, `ConjecturePress`, the panel's
 * `PanelBlock`), the same dispatcher where it did not (`pressVerb`), with its
 * price, its key, its refusal and its demo mark. Nothing here decides anything:
 * a mode only chooses which presses are on screen. The keys act on the selection
 * whatever mode is showing (App's handler), and the forms the free pair opens —
 * the recorded result, the rewrite — still open on the card, where what they
 * write is read.
 */

import { useId, useMemo, useRef } from 'react'
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import { lockHeld, useStore } from '../core/store'
import { OP_HINT, type ReasonNode } from '../core/types'
import { STATUS_WORD } from '../core/vocabulary'
import { upNextRoom } from '../core/upNext'
import { calibrate, pendingPointer, pendingPointerLine } from '../core/calibration'
import { DEMO_SESSION_ID, loadOutcomeRecords, outcomeRowsOf } from '../core/persist'
import { projectVerbUsd } from '../engine/run'
import { cancelCurrent } from '../engine/reason'
import { demoOpsFor } from '../engine/demo'
import { Icon } from './components/Icon'
import { VerbPrice } from './VerbPrice'
import { pressName, pressTail, sharedWord, useRouteReady } from './pressName'
import { pressVerb } from './upNextPress'
import { PanelBlock, SurveyPanel, VerificationPanel, scrollToOutcomeForm } from './Grounding'
import { ConjecturePress, ConsensusPress, ExecutablePress, PriorArtPress } from './ConjectureBlock'
import { SweepPress } from './SweepBlock'
import { DisagreePressFor, scrollToRewriteEditor } from './NodeCard'
import { noteRewriteOpener } from './cardFocus'
import { useVoiceOn } from './useVoiceOn'
import { useFigureBasis } from './useFigureBasis'
import {
  MODE_LABEL,
  modesFor,
  setComposerMode,
  shownMode,
  useComposerMode,
  type ComposerMode,
} from './composerMode'

/** The six one-pass verbs: each wears its pass's tint as a dot, as the canvas's
 *  thought tints do, and the gloss is `OP_HINT`'s, which the palette reads too. */
const VERBS: Array<{ id: keyof typeof OP_HINT; name: string; tint: string; key: string }> = [
  { id: 'deepen', key: 'D', name: 'Deepen', tint: 'var(--sem-signal)' },
  { id: 'challenge', key: 'C', name: 'Challenge', tint: 'var(--sem-tension)' },
  { id: 'diverge', key: 'X', name: 'Diverge', tint: 'var(--sem-gap)' },
  { id: 'experiment', key: 'T', name: 'Test', tint: 'var(--sem-insight)' },
  { id: 'invert', key: 'I', name: 'Invert', tint: 'var(--sem-weak)' },
  { id: 'expand', key: 'B', name: 'Branch', tint: 'var(--accent)' },
]

/**
 * The mode showing for a node: the reader's choice where the node offers it, Ask
 * where it does not (Record, on the seed). Null with nothing selected — there are
 * no modes then, and the host's box is all there is.
 */
export function useNodeMode(node: ReasonNode | null): ComposerMode | null {
  const chosen = useComposerMode()
  return node ? shownMode(node, chosen) : null
}

/**
 * A mode's presses, for every mode but Ask — whose panel is the host's own box
 * (the composer's on a desk, the sheet's on a phone), carrying the same `id` the
 * tabs control.
 */
export function ModePanel({
  node,
  mode,
  id,
}: {
  node: ReasonNode
  mode: Exclude<ComposerMode, 'ask'>
  id: string
}) {
  return (
    <div
      className="cmode-panel"
      id={`${id}-panel`}
      role="tabpanel"
      aria-labelledby={`${id}-${mode}`}
    >
      {mode === 'reason' ? (
        <ReasonActs node={node} />
      ) : mode === 'investigate' ? (
        <InvestigateActs node={node} />
      ) : (
        <RecordActs node={node} />
      )}
    </div>
  )
}

/**
 * The four modes as a segmented control: one tab stop, ← → between the modes,
 * Home and End to the ends — the tab pattern's keys. A mode the node does not
 * offer (Record, on the seed) is not drawn.
 */
export function ModeTabs({ node, mode, id }: { node: ReasonNode; mode: ComposerMode; id: string }) {
  const modes = modesFor(node)
  const list = useRef<HTMLDivElement | null>(null)
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const at = modes.indexOf(mode)
    const to =
      e.key === 'ArrowRight'
        ? (at + 1) % modes.length
        : e.key === 'ArrowLeft'
          ? (at - 1 + modes.length) % modes.length
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? modes.length - 1
              : -1
    if (to < 0) return
    e.preventDefault()
    setComposerMode(modes[to]!)
    requestAnimationFrame(() =>
      list.current?.querySelector<HTMLButtonElement>(`[data-mode="${modes[to]}"]`)?.focus(),
    )
  }
  return (
    <div
      ref={list}
      className="cmode-tabs"
      role="tablist"
      aria-label="What to do with this finding"
      onKeyDown={onKeyDown}
    >
      {modes.map((m) => (
        <button
          key={m}
          type="button"
          role="tab"
          id={`${id}-${m}`}
          data-mode={m}
          aria-selected={m === mode}
          aria-controls={`${id}-panel`}
          tabIndex={m === mode ? 0 : -1}
          onClick={() => setComposerMode(m)}
        >
          {MODE_LABEL[m]}
        </button>
      ))}
    </div>
  )
}

/**
 * Stop, inside a mode with no box: the composer's primary is Stop while a lone
 * pass is in flight, and on Reason, Investigate and Record that primary is not on
 * screen. The same press — `cancelCurrent` — and the same rule: a run is stopped
 * from its own strip, never from here (D179's one Stop per run).
 */
function ModeFoot({ children }: { children?: ReactNode }) {
  const locked = useStore(lockHeld)
  const runLive = useStore((s) => s.run !== null && s.run.phase !== 'ended')
  const stopping = locked && !runLive
  if (!children && !stopping) return null
  return (
    <div className="cmode-foot">
      <span className="cmode-cap">{children}</span>
      {stopping && (
        <button
          type="button"
          className="btn btn--outline cmode-stop"
          aria-label="Stop the pass in flight"
          title="Stop this pass — what has already arrived is kept, and it was billed either way"
          onClick={cancelCurrent}
        >
          <Icon name="stop" size={12} />
          Stop
        </button>
      )}
    </div>
  )
}

/**
 * Reason: the six one-pass verbs, three by two, with the price once, under them
 * — every cell is one pass at one arithmetic. Under a pass in flight they stay
 * pressable while Up next has room: the press queues behind it.
 */
function ReasonActs({ node }: { node: ReasonNode }) {
  const locked = useStore(lockHeld)
  const room = useStore(upNextRoom)
  const ready = useRouteReady('deepen')
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const moveEst = projectVerbUsd({ modelId, roleModels }, 'move')
  useVoiceOn()
  useFigureBasis()
  // In demo mode only some branches are authored; marked, rather than every
  // button leading to the same key prompt.
  const demoOps = !ready ? demoOpsFor(node.id) : null
  return (
    <>
      <div className="actions actions--verbs cmode-verbs">
        {VERBS.map((op) => (
          <button
            key={op.id}
            type="button"
            className="action"
            style={{ '--act-tint': op.tint } as CSSProperties}
            // Shut by a full Up next line, not disabled: focus stays, and the
            // press says why in the line's own sentence (`pressVerb`).
            aria-disabled={(locked && !room) || undefined}
            aria-label={pressName(
              op.name,
              pressTail({ ready, demo: demoOps?.includes(op.id), est: moveEst }),
            )}
            aria-keyshortcuts={op.key}
            onClick={() => pressVerb(op.id, node.id)}
            title={
              demoOps && !demoOps.includes(op.id)
                ? `${OP_HINT[op.id]}. Not part of the demo — connect a key to run it.`
                : OP_HINT[op.id]
            }
          >
            <b>
              <i className="cmode-dot" aria-hidden />
              <span className="action-name">{op.name}</span>
              <span className="kbd vp-kbd">{op.key}</span>
              {demoOps?.includes(op.id) && (
                <span
                  className="action-demo"
                  aria-label="available in the demo"
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    flex: 'none',
                    background: 'var(--sem-insight)',
                  }}
                />
              )}
            </b>
          </button>
        ))}
      </div>
      <ModeFoot>
        One pass each{' '}
        <VerbPrice
          verb="move"
          instead={
            ready
              ? undefined
              : sharedWord(VERBS.filter((op) => demoOps?.includes(op.id)).length, VERBS.length)
          }
        />
      </ModeFoot>
    </>
  )
}

/**
 * Investigate: the passes that look outside the graph or ask again — the source
 * check first, because whether a claim is true is the question every card is
 * read for (on the seed, the survey; on an insight, its prior-art search) — then
 * the insights pass, the field sweep, the comparison of a disagreeing pair, and
 * the second opinion where the instruments are on. Each is the press the card
 * made, priced on its own row.
 */
function InvestigateActs({ node }: { node: ReasonNode }) {
  const session = useStore((s) => s.session)
  const instruments = useStore((s) => s.instruments)
  const isRoot = node.parentId === null
  return (
    <>
      <div className="actions cmode-wide">
        {isRoot ? (
          <SurveyPanel node={node} part="press" />
        ) : node.conjecture ? (
          <PriorArtPress node={node} />
        ) : (
          <VerificationPanel session={session} node={node} part="press" />
        )}
        <ConjecturePress node={node} />
        {node.conjecture && <ConsensusPress session={session} node={node} />}
        {node.conjecture && <ExecutablePress session={session} node={node} />}
        <SweepPress node={node} />
        {!isRoot && <DisagreePressFor session={session} node={node} />}
        {!isRoot && instruments && <PanelBlock session={session} node={node} part="press" />}
      </div>
      <ModeFoot />
    </>
  )
}

/**
 * Record: the two free acts, which call no model — what happened, and the
 * reader's own words for the claim. Each opens its form on the card, where what
 * it writes is read: the result under the claim, the editor in the claim's place.
 */
function RecordActs({ node }: { node: ReasonNode }) {
  const session = useStore((s) => s.session)
  const pokeRecord = useStore((s) => s.pokeRecord)
  const pokeRewrite = useStore((s) => s.pokeRewrite)
  const noteId = useId()
  // What a result recorded here would count toward, said before one is — the
  // pointer under a recorded result reads the same ledger (`pendingPointer`).
  // Keyed on this exploration's results, not the session object, which changes
  // with every streamed token: the ledger parses every stored exploration.
  const resultsHere = outcomeRowsOf(session)
    .map((r) => `${r.nodeId}:${r.bearing}:${r.recordedAt}`)
    .join()
  const pending = useMemo(
    () =>
      node.outcome
        ? null
        : pendingPointer(
            calibrate(loadOutcomeRecords(session)),
            node,
            session.id === DEMO_SESSION_ID,
          ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      session.id,
      resultsHere,
      !node.outcome,
      node.model?.provider,
      node.model?.id,
      node.model?.name,
      node.origin,
      node.editedAt,
    ],
  )
  return (
    <>
      <div className="actions cmode-wide">
        <button
          type="button"
          className="action action--wide"
          style={{ '--act-tint': 'var(--ink-insight)' } as CSSProperties}
          aria-keyshortcuts="R"
          aria-label={pressName('Record what happened', STATUS_WORD.free.toLowerCase())}
          aria-describedby={noteId}
          title="You ran it, read it, or watched it. Write down what you observed — it calls no model, spends nothing, and outranks everything the model said about this claim. Kept verbatim."
          onClick={() => {
            pokeRecord()
            scrollToOutcomeForm(node.id)
          }}
        >
          <b style={{ width: '100%' }}>
            <Icon name="eye" size={13} />
            Record what happened
            <span className="vp">
              <span className="kbd" aria-hidden>
                R
              </span>
              <span className="vp-spent">{STATUS_WORD.free}</span>
            </span>
          </b>
          <span id={noteId}>
            {node.outcome
              ? 'Free — no model call. What you record outranks everything the model said.'
              : 'Free — no model call. Nothing observed has been recorded against this claim; a result you record outranks everything the model said.'}
          </span>
        </button>
        <button
          type="button"
          className="action action--wide"
          style={{ '--act-tint': 'var(--accent)' } as CSSProperties}
          // The link's name, as it was on the card: writing is free, and the paid
          // step is the editor's own button, priced there.
          aria-label="Rewrite this understanding"
          aria-keyshortcuts="E"
          title="Correct it, sharpen it, or replace it. Writing costs nothing; pressing re-derive afterwards is the paid pass, and the editor prices it on its own button. Your words replace the model's."
          onClick={(e) => {
            noteRewriteOpener(e.currentTarget)
            pokeRewrite()
            scrollToRewriteEditor(node.id)
          }}
        >
          <b style={{ width: '100%' }}>
            <Icon name="edit" size={13} />
            Rewrite this understanding
            <span className="vp">
              <span className="kbd" aria-hidden>
                E
              </span>
            </span>
          </b>
          <span>
            Your words replace the model’s. Writing is free; re-deriving what stands on it is the
            paid pass, priced on the editor’s own button.
          </span>
        </button>
      </div>
      {/* What a settled result would count toward, said under the pair and at rest,
          as the card said it under its Record row: a row here is one line, and its
          sentence is only its hover's and a screen reader's. */}
      <ModeFoot>{pending && pendingPointerLine(pending)}</ModeFoot>
    </>
  )
}
